import { useCallback, useEffect, useMemo, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "../lib/firebase";
import { ensureUserProfile } from "../lib/data/users";
import { reconcilePendingNotifications } from "../lib/data/notification-events";
import { AuthContext } from "./useAuth";
import { fetchBannedEmail } from "../lib/data/bans";
import { normalizeEmail } from "../lib/utils";

const bannedEmailKey = "qs_banned_email";
const bannedReasonKey = "qs_banned_reason";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileReady, setProfileReady] = useState(false);
  const [banned, setBanned] = useState(null);

  const refreshUser = useCallback(async () => {
    if (!auth.currentUser) return null;
    await auth.currentUser.reload();
    setUser(auth.currentUser);
    return auth.currentUser;
  }, []);

  useEffect(() => {
    let isMounted = true;
    const unsubscribe = onAuthStateChanged(auth, (nextUser) => {
      const run = async () => {
        if (!isMounted) return;
        setLoading(true);
        setProfileReady(false);
        if (!nextUser) {
          const storedEmail = localStorage.getItem(bannedEmailKey);
          const storedReason = localStorage.getItem(bannedReasonKey);
          if (storedEmail) {
            setBanned({ email: storedEmail, reason: storedReason || "suspended" });
          } else {
            setBanned(null);
          }
          setUser(null);
          setProfileReady(false);
          setLoading(false);
          return;
        }

        setUser(nextUser);
        setLoading(false);

        let profileReadySet = false;
        const profileTimeout = setTimeout(() => {
          if (!isMounted || profileReadySet) return;
          profileReadySet = true;
          console.warn("Profile sync taking too long; continuing without it.");
          setProfileReady(true);
        }, 2000);

        const email = normalizeEmail(nextUser?.email);
        if (email) {
          const banned = await fetchBannedEmail(email);
          if (banned) {
            localStorage.setItem(bannedEmailKey, banned.email);
            localStorage.setItem(bannedReasonKey, banned.reason);
            setBanned(banned);
            await signOut(auth);
            setUser(null);
            setProfileReady(false);
            setLoading(false);
            clearTimeout(profileTimeout);
            return;
          }
        }

        localStorage.removeItem(bannedEmailKey);
        localStorage.removeItem(bannedReasonKey);
        setBanned(null);
        try {
          // Anonymous guests (link-shared scheduler voters) get NO profile
          // docs: skip ensureUserProfile + reconcilePendingNotifications so no
          // users/{anonUid} or usersPublic/{anonUid} skeleton is created. The
          // finally still sets profileReady=true so route guards don't hang.
          if (!nextUser.isAnonymous) {
            await ensureUserProfile(nextUser);
            try {
              await reconcilePendingNotifications();
            } catch (err) {
              console.warn("Failed to reconcile pending notifications:", err);
            }
          }
        } catch (err) {
          console.error("Failed to ensure user profile:", err);
        } finally {
          if (!profileReadySet) {
            clearTimeout(profileTimeout);
            profileReadySet = true;
            setProfileReady(true);
          }
        }
      };
      run().catch((err) => {
        // Serialize the error explicitly: Chromium expands an Error object in
        // console.error to include its code+message (e.g. "...code=unavailable
        // ...client is offline"), but Firefox renders a bare "Error" with no
        // detail. Stringifying makes the logged text identical across engines,
        // so the transient boot-time Firestore-offline blip matches the same
        // allowlist entry everywhere — and a genuine (non-offline) failure
        // still logs its real, gate-tripping message.
        console.error("Auth state check failed:", String(err));
        setUser(nextUser);
        setProfileReady(true);
        setLoading(false);
      });
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  const value = useMemo(
    () => ({ user, loading, banned, profileReady, refreshUser }),
    [user, loading, banned, profileReady, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
